/**
 * Trades view - single table with status filter pills.
 *
 * Status values stored in the DB stay as "Proposed" / "Accepted" / "Declined"
 * / "Cancelled" for back-compat with everything that already reads or writes
 * them. The UI labels them as Pending / Completed / Declined / Cancelled.
 *
 * Only Completed (DB: "Accepted") trades feed financial totals - the KPI
 * strip, Schedule C, cash flow, and Books Health. Pending and archived trades
 * are records that don't move books until promoted to Completed.
 *
 * Net G/L is (In FMV - Out FMV) + cash boot. Realized Gain is the taxable
 * disposition figure on outgoing items (Completed only).
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { downloadCsv } from "../lib/csv";
import { todayISO } from "../utils/format";
import { createRowSelection } from "../components/rowSelection";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar } from "../utils/format";
import type { Row } from "../lib/types";

const STATUS_FILTER_KEY = "enigma_trades_status_filter_v1";

const sel = createRowSelection("trades");

function tradesEdit(id: string): void { (window as any).editTrade?.(id); }
function tradesDelete(id: string): void { (window as any).deleteRow?.("trades", "Trade ID", id); }
function tradesPrint(id: string): void { (window as any).exportTradeProposal?.(id); }

interface TradeRowView extends Row {
  _outBasis: number;
  _outFMV: number;
  _inFMV: number;
  _gl: number;
  _realized: number;
  _hasUnknownHolding: boolean;
}

function normalizeTrade(t: Row): TradeRowView {
  const out: any[] = Array.isArray(t.Outgoing) ? t.Outgoing : [];
  const inc: any[] = Array.isArray(t.Incoming) ? t.Incoming : [];
  const v = t as TradeRowView;
  v._outBasis = out.reduce((s, x) => s + (Number(x.Basis) || 0) * (Number(x.Qty) || 1), 0);
  v._outFMV   = out.reduce((s, x) => s + (Number(x.FMV)   || 0) * (Number(x.Qty) || 1), 0);
  v._inFMV    = inc.reduce((s, x) => s + (Number(x.FMV)   || 0) * (Number(x.Qty) || 1), 0);
  v._gl       = +(v._inFMV - v._outFMV + (Number(t["Cash"]) || 0)).toFixed(2);
  v._realized = t["Realized Gain"] != null
    ? +Number(t["Realized Gain"]).toFixed(2)
    : +out.reduce((s, x) => s + ((Number(x.FMV) || 0) - (Number(x.Basis) || 0)) * (Number(x.Qty) || 1), 0).toFixed(2);
  v._hasUnknownHolding = out.some(x => x["Tax Treatment"] === "Unknown" || (x["Lot ID"] && !x["Acq Date"]));
  return v;
}

function summarizeTradeItems(items: any[] | undefined): string {
  if (!Array.isArray(items) || !items.length) return `<span class="text-[var(--muted)]">-</span>`;
  if (items.length === 1) {
    const x = items[0];
    return escapeHtml((x.Item || x["Lot ID"] || "?") + ((x.Qty || 1) > 1 ? ` x${x.Qty}` : ""));
  }
  const totalQty = items.reduce((s, x) => s + (Number(x.Qty) || 1), 0);
  const titles = items.map(x => `${x.Item || x["Lot ID"] || "?"}${(x.Qty || 1) > 1 ? ` x${x.Qty}` : ""}`).join("\n");
  return `<span title="${escapeHtml(titles)}"><b>${items.length}</b> items <span class="text-[var(--muted)]">(${totalQty} units)</span></span>`;
}

function statusOf(t: Row): string { return String(t["Status"] || "Accepted"); }

function statusLabel(status: string): string {
  if (status === "Proposed") return "Pending";
  if (status === "Accepted") return "Completed";
  return status;
}

function statusPillHtml(status: string): string {
  const cls = status === "Proposed"  ? "pill pill-amber"
            : status === "Accepted"  ? "pill pill-blue"
            : status === "Declined"  ? "pill pill-red"
            : "pill pill-gray";
  return `<span class="${cls} text-xs">${escapeHtml(statusLabel(status))}</span>`;
}

function getStatusFilter(): string {
  const v = localStorage.getItem(STATUS_FILTER_KEY);
  return v === "pending" || v === "completed" || v === "archived" ? v : "all";
}

function matchesStatusFilter(filter: string, status: string): boolean {
  if (filter === "all")       return true;
  if (filter === "pending")   return status === "Proposed";
  if (filter === "completed") return status === "Accepted";
  if (filter === "archived")  return status === "Declined" || status === "Cancelled";
  return true;
}

function filterPillHtml(value: string, label: string, count: number, current: string): string {
  const active = value === current;
  const cls = active ? "pill pill-blue" : "pill pill-gray";
  return `<button type="button" class="${cls} text-xs cursor-pointer hover:opacity-90 transition-opacity" onclick="setTradesStatusFilter('${value}')">${label} <span class="opacity-70">(${count})</span></button>`;
}

/** Download the currently-filtered trades as a raw-value CSV. */
export function exportTradesCsv(): void {
  const all = DB.table("trades").slice().reverse().map(normalizeTrade);
  const filter = getStatusFilter();
  const rows = all.filter(t => {
    const st = statusOf(t);
    if (filter === "pending")   return st === "Proposed";
    if (filter === "completed") return st === "Accepted";
    if (filter === "archived")  return st === "Declined" || st === "Cancelled";
    return true;
  });
  downloadCsv(`trades-${todayISO()}.csv`, rows, [
    { label: "Trade ID",      get: r => r["Trade ID"] },
    { label: "Date",          get: r => r["Date"] },
    { label: "Status",        get: r => statusOf(r) === "Proposed" ? "Pending" : (statusOf(r) === "Accepted" ? "Completed" : statusOf(r)) },
    { label: "Out Items",     get: r => (Array.isArray(r.Outgoing) ? r.Outgoing.length : 0) },
    { label: "Outgoing FMV",  get: r => r["Outgoing FMV"] },
    { label: "Outgoing Basis",get: r => r["Outgoing Basis"] },
    { label: "In Items",      get: r => (Array.isArray(r.Incoming) ? r.Incoming.length : 0) },
    { label: "Incoming FMV",  get: r => r["Incoming FMV"] },
    { label: "Cash (+recv/-paid)", get: r => r["Cash"] },
    { label: "Realized Gain", get: r => r["Realized Gain"] },
    { label: "Notes",         get: r => r["Notes"] },
  ]);
}
(window as any).exportTradesCsv = exportTradesCsv;

export function viewTrades(root: HTMLElement): void {
  const all = DB.table("trades").slice().reverse().map(normalizeTrade);
  const counts = {
    all:       all.length,
    pending:   all.filter(t => statusOf(t) === "Proposed").length,
    completed: all.filter(t => statusOf(t) === "Accepted").length,
    archived:  all.filter(t => statusOf(t) === "Declined" || statusOf(t) === "Cancelled").length,
  };
  const filter = getStatusFilter();
  const filtered = all.filter(t => matchesStatusFilter(filter, statusOf(t)));
  const total = filtered.length;
  const pageRows = slicePage("trades", filtered, 50);

  // KPIs always reflect Completed trades (the financial reality).
  const completed = all.filter(t => statusOf(t) === "Accepted");
  const totalOutFMV   = sum(completed, t => t._outFMV);
  const totalInFMV    = sum(completed, t => t._inFMV);
  const totalGL       = sum(completed, t => t._gl);
  const totalRealized = sum(completed, t => t._realized);

  root.innerHTML = `
    ${kpiBar([
      { label: "Completed",     value: fmtN(completed.length) },
      { label: "Outgoing FMV",  value: fmt$0(totalOutFMV) },
      { label: "Incoming FMV",  value: fmt$0(totalInFMV) },
      { label: "Net G/L",       value: fmt$0(totalGL),       tone: totalGL >= 0 ? "green" : "red" },
      { label: "Realized Gain", value: fmt$0(totalRealized), tone: totalRealized >= 0 ? "green" : "red" },
    ])}

    <div class="text-xs text-[var(--muted)] mb-3">KPIs above reflect <b>Completed</b> trades only - those moved inventory and booked realized gain. <b>Pending</b> trades are drafts that don't touch your books until you click <b>Complete</b> on the row.</div>

    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      <div class="flex flex-wrap gap-1">
        ${filterPillHtml("all",       "All",       counts.all,       filter)}
        ${filterPillHtml("pending",   "Pending",   counts.pending,   filter)}
        ${filterPillHtml("completed", "Completed", counts.completed, filter)}
        ${filterPillHtml("archived",  "Archived",  counts.archived,  filter)}
      </div>
      <div class="ml-auto flex flex-wrap gap-2 items-center">
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportTradesCsv()" title="Download the currently-filtered trades as a CSV (raw values)">${icon("download")} CSV</button>
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,     title: "Edit selected row",  handler: tradesEdit },
        { id: "print",  label: `${icon("printer")} Print`, title: "Print selected",     handler: tradesPrint },
        { id: "delete", label: `${icon("trash")} Delete`,  title: "Delete selected row", danger: true, handler: tradesDelete },
      ])}</div>
    </div>

    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Trade ID</th><th>Date</th><th>Status</th>
          <th>Outgoing</th><th class="text-right">Out FMV</th>
          <th>Incoming</th><th class="text-right">In FMV</th>
          <th class="text-right">Cash</th>
          <th class="text-right" title="Net value swing incl. cash boot: (In FMV - Out FMV) + cash">Net G/L</th>
          <th class="text-right" title="Taxable disposition (Completed only): sum((FMV - Basis) * Qty) on outgoing items">Realized Gain</th>
          <th>Notes</th><th></th>
        </tr></thead>
        <tbody>
          ${pageRows.map(t => {
            const id = escapeHtml(t["Trade ID"] || "");
            const st = statusOf(t);
            const cash = Number(t["Cash"]) || 0;
            const isCompleted = st === "Accepted";
            const warn = t._hasUnknownHolding
              ? `<span class="pill pill-amber ml-1" title="One or more outgoing items has no Acq Date - holding period unknown. Fix the source lot for accurate short/long-term classification.">!</span>`
              : "";
            return `<tr ${sel.rowAttrs(id, "editTrade('" + id + "')")}>
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${id}')">${id}</a>${warn}</td>
              <td>${escapeHtml(fmtDate(t["Date"]))}</td>
              <td>${statusPillHtml(st)}</td>
              <td class="text-xs">${summarizeTradeItems(t.Outgoing)}</td>
              <td class="text-right stat-num">${fmt$(t._outFMV)}</td>
              <td class="text-xs">${summarizeTradeItems(t.Incoming)}</td>
              <td class="text-right stat-num">${fmt$(t._inFMV)}</td>
              <td class="text-right stat-num">${fmt$(cash)}</td>
              <td class="text-right stat-num">${fmt$(t._gl)}</td>
              <td class="text-right stat-num">${isCompleted ? fmt$(t._realized) : '<span class="text-[var(--muted)]">&mdash;</span>'}</td>
              <td class="text-xs text-[var(--muted)]" title="${escapeHtml(t["Notes"] || "")}">${escapeHtml(t["Notes"] || "")}</td>
              <td class="text-right whitespace-nowrap">
                <button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="event.stopPropagation(); exportTradeProposal('${id}')" title="Open a printable view">${icon("printer")}</button>
                ${st === "Proposed" ? `<button class="btn btn-primary !py-0.5 !px-2 text-xs ml-1" onclick="event.stopPropagation(); quickAcceptProposal('${id}')" title="Complete this trade now - decrement outgoing lots, create incoming lots, book realized gain. Dated today.">Complete</button>` : ""}
              </td>
            </tr>`;
          }).join("") || `<tr><td colspan="12" class="text-center text-[var(--muted)] py-8">No trades match this filter.${counts.all === 0 ? " Use + New Trade in the header to start one." : ""}</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("trades", total, 50)}
  `;
  sel.applyUI();
}

export function setTradesStatusFilter(value: string): void {
  localStorage.setItem(STATUS_FILTER_KEY, value);
  (window as any).render?.();
}
(window as any).setTradesStatusFilter = setTradesStatusFilter;
